import { Fragment, useMemo, useState } from "react";
import type { ReactNode } from "react";
import classNames from "classnames";

import type { PlaceholderCodeConfig, PlaceholderDef } from "../../blockTypes";
import { BLOCK_MESSAGES } from "../../messages";
import BlockRenderer from "./BlockRenderer";

/**
 * PlaceholderCodeBlock
 *
 * Web version of the Android PlaceholderCodeView:
 *  - the options are shuffled once and live in a bank below the code
 *  - tapping a bank option fills the first empty placeholder
 *  - tapping a filled placeholder sends its option back to the END of the bank
 *  - backspace removes the last filled placeholder, reset removes all of them
 *
 * The options are shuffled on mount, so render it with a `key` per question.
 */

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

// Splits a template line while keeping the [[KEY]] tokens in the result
const TOKEN_SPLIT_REGEX = /(\[\[[A-Za-z0-9_]+\]\])/g;
const TOKEN_MATCH_REGEX = /^\[\[([A-Za-z0-9_]+)\]\]$/;
const TOKEN_REPLACE_REGEX = /\[\[([A-Za-z0-9_]+)\]\]/g;

// Every token (in the bank and inside a placeholder) shares the same shape
const CHIP_BASE =
    "inline-flex min-h-[54px] min-w-[50px] items-center justify-center " +
    "rounded-xl border-2 px-3 font-mono text-base font-bold text-black " +
    "transition active:scale-95 disabled:cursor-default disabled:active:scale-100";

const CHIP_IDLE = "border-gray-300 bg-white hover:border-gray-400";
const CHIP_CORRECT = "border-green-500 bg-green-100";
const CHIP_WRONG = "border-red-500 bg-red-100";

const EMPTY_SLOT_CLASSES =
    "mx-0.5 inline-block min-h-[54px] min-w-[50px] rounded-xl " +
    "border-2 border-dashed border-gray-500 bg-white/5";

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

// Options get an id (their index) so duplicates, e.g. two "(" tokens, stay distinguishable
interface NormalizedOption {
    id: number;
    text: string;
}

// slots: placeholder key -> id of the option placed inside it
// bank:  ids of the options still available, in display order
interface Board {
    slots: Record<string, number>;
    bank: number[];
}

type RunCode = (language: string, code: string) => Promise<{ stdout?: string }>;

interface PlaceholderCodeBlockProps {
    config?: PlaceholderCodeConfig;
    onComplete?: () => void; // Called when the check succeeds
    // When given and config.showCompiler is true, a run button appears after the check
    runCode?: RunCode;
    onAnswer?: (isCorrect: boolean) => void;
}

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

// Fisher-Yates shuffle (returns a new array)
const shuffle = (items: number[]): number[] => {
    const copy = [...items];

    for (let i = copy.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
    }

    return copy;
};

// Placeholder keys are matched case-insensitively, like on Android
const slotKey = (key: string): string => key.toUpperCase();

// Empties the given slots and appends their options to the end of the bank
const releaseSlots = ({ slots, bank }: Board, keys: string[]): Board => {
    const remaining = { ...slots };
    const returned: number[] = [];

    keys.forEach((key) => {
        if (remaining[key] !== undefined) {
            returned.push(remaining[key]);
            delete remaining[key];
        }
    });

    return { slots: remaining, bank: [...bank, ...returned] };
};

/* -------------------------------------------------------------------------- */
/*                               Small components                             */
/* -------------------------------------------------------------------------- */

const Icon = ({ children }: { children: ReactNode }) => (
    <svg
        viewBox="0 0 24 24"
        width="20"
        height="20"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        {children}
    </svg>
);

interface IconButtonProps {
    label: string;
    disabled: boolean;
    onClick: () => void;
    children: ReactNode;
}

const IconButton = ({ label, disabled, onClick, children }: IconButtonProps) => (
    <button
        type="button"
        aria-label={label}
        title={label}
        disabled={disabled}
        onClick={onClick}
        className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition hover:text-white disabled:opacity-30 disabled:hover:text-gray-400"
    >
        {children}
    </button>
);

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

const PlaceholderCodeBlock = ({ config, onComplete, runCode, onAnswer }: PlaceholderCodeBlockProps) => {
    const {
        template = "",
        options = [],
        placeholders = [],
        prompt,
        explanation,
        language = "",
        showCompiler = false
    } = config ?? {};

    const normalizedOptions = useMemo<NormalizedOption[]>(
        () =>
            options.map((option, index) => ({
                id: index,
                text: typeof option === "string" ? option : option.text ?? option.value ?? ""
            })),
        [options]
    );

    const [board, setBoard] = useState<Board>(() => ({
        slots: {},
        bank: shuffle(normalizedOptions.map((option) => option.id))
    }));
    const { slots, bank } = board;

    const [checked, setChecked] = useState(false);
    const [output, setOutput] = useState<string | null>(null); // null = output card hidden
    const [running, setRunning] = useState(false);

    /* ------------------------------ Derived state ----------------------------- */

    const optionText = (id?: number): string | undefined =>
        id === undefined ? undefined : normalizedOptions[id]?.text;

    const selectedText = (placeholder: PlaceholderDef): string | undefined =>
        optionText(slots[slotKey(placeholder.key)]);

    const isSlotCorrect = (placeholder: PlaceholderDef): boolean =>
        selectedText(placeholder) === placeholder.correctOptionText;

    // Keys of the filled placeholders, in template order
    const filledKeys = placeholders
        .map((placeholder) => slotKey(placeholder.key))
        .filter((key) => slots[key] !== undefined);

    const allFilled = filledKeys.length === placeholders.length;
    const allCorrect = placeholders.every(isSlotCorrect);
    const canRun = showCompiler && checked && typeof runCode === "function";

    const promptBlocks = prompt?.blocks ?? [];
    const explanationBlocks = explanation?.blocks ?? [];

    /* -------------------------------- Handlers -------------------------------- */

    // Bank option tapped: it goes into the first empty placeholder
    const handleOptionClick = (optionId: number) => {
        if (checked) return;

        setBoard((current) => {
            const target = placeholders.find(
                (placeholder) => current.slots[slotKey(placeholder.key)] === undefined
            );

            if (!target) return current;

            return {
                slots: { ...current.slots, [slotKey(target.key)]: optionId },
                bank: current.bank.filter((id) => id !== optionId)
            };
        });
    };

    // Filled placeholder tapped: its option returns to the bank
    const handleSlotClick = (key: string) => {
        if (checked) return;
        setBoard((current) => releaseSlots(current, [key]));
    };

    // Removes the last filled placeholder
    const handleBackspace = () => {
        if (checked || filledKeys.length === 0) return;
        setBoard((current) => releaseSlots(current, [filledKeys[filledKeys.length - 1]]));
    };

    // Removes every filled placeholder
    const handleReset = () => {
        if (checked) return;
        setBoard((current) => releaseSlots(current, filledKeys));
    };

    const handleCheck = () => {
        setChecked(true);
        onAnswer?.(allCorrect);
        if (allCorrect) onComplete?.();
    };

    // After a wrong check: keep the correct tokens, return the wrong ones
    const handleRetry = () => {
        const wrongKeys = placeholders
            .filter((placeholder) => !isSlotCorrect(placeholder))
            .map((placeholder) => slotKey(placeholder.key));

        setBoard((current) => releaseSlots(current, wrongKeys));
        setOutput(null);
        setChecked(false);
    };

    // Fills the template with the selected tokens (unknown tokens stay as they are)
    const compileCode = (): string =>
        template.replace(TOKEN_REPLACE_REGEX, (token: string, key: string) => {
            const placeholder = placeholders.find((item) => slotKey(item.key) === key.toUpperCase());

            return placeholder ? selectedText(placeholder) ?? "" : token;
        });

    const handleRun = async () => {
        if (!runCode) return;

        setRunning(true);
        setOutput(BLOCK_MESSAGES.running);

        try {
            const { stdout } = await runCode(language, compileCode());
            setOutput(stdout || BLOCK_MESSAGES.noOutput);
        } catch {
            setOutput(BLOCK_MESSAGES.runFailed);
        } finally {
            setRunning(false);
        }
    };

    /* -------------------------------- Rendering ------------------------------- */

    const renderChip = (optionId: number, onClick: () => void, stateClasses = CHIP_IDLE) => (
        <button
            type="button"
            disabled={checked}
            onClick={onClick}
            className={classNames(CHIP_BASE, stateClasses)}
        >
            {optionText(optionId)}
        </button>
    );

    // One placeholder: a dashed box when empty, the chosen chip when filled
    const renderSlot = (placeholder: PlaceholderDef, key: string) => {
        const slot = slotKey(placeholder.key);
        const optionId = slots[slot];

        if (optionId === undefined) {
            return <span key={key} aria-hidden="true" className={EMPTY_SLOT_CLASSES} />;
        }

        // After the check the chip shows whether it was right
        const stateClasses = !checked
            ? CHIP_IDLE
            : isSlotCorrect(placeholder)
                ? CHIP_CORRECT
                : CHIP_WRONG;

        return (
            <span key={key} className="mx-0.5 inline-flex">
                {renderChip(optionId, () => handleSlotClick(slot), stateClasses)}
            </span>
        );
    };

    // Every template line is its own row of text pieces and placeholders
    const renderTemplate = () =>
        template.split("\n").map((line, lineIndex) => (
            <div
                key={lineIndex}
                className={classNames("flex items-center whitespace-pre", !line && "h-4")}
            >
                {line.split(TOKEN_SPLIT_REGEX).map((part, partIndex) => {
                    const key = `${lineIndex}-${partIndex}`;

                    // split() leaves empty strings around the tokens
                    if (!part) return null;

                    const match = part.match(TOKEN_MATCH_REGEX);

                    // Regular code text
                    if (!match) return <span key={key}>{part}</span>;

                    const placeholder = placeholders.find(
                        (item) => slotKey(item.key) === match[1].toUpperCase()
                    );

                    // Unknown placeholder: render the raw token so the content bug is visible
                    if (!placeholder) return <span key={key}>{part}</span>;

                    return renderSlot(placeholder, key);
                })}
            </div>
        ));

    return (
        <div className="w-full">
            {/* Prompt */}
            {promptBlocks.length > 0 && (
                <div className="mb-6">
                    {promptBlocks.map((block, index) => (
                        <BlockRenderer key={block.id ?? index} block={block} />
                    ))}
                </div>
            )}

            {/* Code card (always left-to-right, even inside an RTL page) */}
            <div
                dir="ltr"
                className="rounded-2xl bg-gray-900 p-4 text-left font-mono text-sm text-gray-100"
            >
                <div className="mb-3 flex items-center justify-between">
                    <span className="text-xs text-gray-400">{language}</span>

                    <div className="flex items-center gap-1">
                        {canRun && (
                            <button
                                type="button"
                                onClick={handleRun}
                                disabled={running}
                                className="me-2 rounded-lg bg-green-600 px-3 py-1 font-sans text-xs font-bold text-white transition hover:bg-green-700 disabled:opacity-50"
                            >
                                {BLOCK_MESSAGES.run}
                            </button>
                        )}

                        <IconButton
                            label={BLOCK_MESSAGES.removeLast}
                            disabled={checked || filledKeys.length === 0}
                            onClick={handleBackspace}
                        >
                            <Icon>
                                <path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" />
                                <line x1="18" y1="9" x2="12" y2="15" />
                                <line x1="12" y1="9" x2="18" y2="15" />
                            </Icon>
                        </IconButton>

                        <IconButton
                            label={BLOCK_MESSAGES.clearAll}
                            disabled={checked || filledKeys.length === 0}
                            onClick={handleReset}
                        >
                            <Icon>
                                <polyline points="1 4 1 10 7 10" />
                                <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                            </Icon>
                        </IconButton>
                    </div>
                </div>

                {/* Long lines scroll instead of wrapping */}
                <div className="overflow-x-auto">
                    <div className="w-max min-w-full">{renderTemplate()}</div>
                </div>
            </div>

            {/* Output card (shown after a run) */}
            {output !== null && (
                <pre
                    dir="auto"
                    className="mt-4 whitespace-pre-wrap rounded-2xl bg-black p-4 font-mono text-sm text-green-400"
                >
                    {output}
                </pre>
            )}

            {/* Options bank */}
            <div
                dir="ltr"
                className="mt-4 flex min-h-[86px] flex-wrap content-start justify-center gap-x-1 gap-y-3 rounded-2xl border-2 border-gray-200 bg-white p-3"
            >
                {bank.map((optionId) => (
                    <Fragment key={optionId}>
                        {renderChip(optionId, () => handleOptionClick(optionId))}
                    </Fragment>
                ))}
            </div>

            {/* Check button */}
            {!checked && (
                <button
                    type="button"
                    onClick={handleCheck}
                    disabled={!allFilled}
                    className="mt-6 w-full rounded-xl bg-blue-600 px-6 py-3 font-bold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {BLOCK_MESSAGES.check}
                </button>
            )}

            {/* Result */}
            {checked && (
                <div
                    role="status"
                    className={classNames(
                        "mt-6 flex items-center justify-between gap-4 rounded-xl p-4",
                        allCorrect ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                    )}
                >
                    <span>
                        {allCorrect
                            ? BLOCK_MESSAGES.placeholderCorrect
                            : BLOCK_MESSAGES.placeholderWrong}
                    </span>

                    {/* Only wrong answers can be retried */}
                    {!allCorrect && (
                        <button
                            type="button"
                            onClick={handleRetry}
                            className="rounded-lg bg-red-600 px-4 py-2 font-bold text-white hover:bg-red-700"
                        >
                            {BLOCK_MESSAGES.retry}
                        </button>
                    )}
                </div>
            )}

            {/* Explanation */}
            {checked && explanationBlocks.length > 0 && (
                <div className="mt-6">
                    {explanationBlocks.map((block, index) => (
                        <BlockRenderer key={block.id ?? index} block={block} />
                    ))}
                </div>
            )}
        </div>
    );
};

export default PlaceholderCodeBlock;